import { useEffect, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getChannels } from "../Services/MasterDataService";

const TABS = [
    { key: "channels", label: "Channels" },
];

const PAGE_SIZE = 10;

function MasterData() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("channels");
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const fetchChannels = async () => {
        setLoading(true);
        try {
            const res = await getChannels();
            if (res?.data?.status === "success") {
                setChannels(res.data.channels ?? []);
            }
        } catch (error) {
            CustomToast("fail", "Failed to load channels. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === "channels") {
            fetchChannels();
        }
    }, [activeTab]);

    useEffect(() => {
        setCurrentPage(1);
    }, [channels.length]);

    const totalPages = Math.max(1, Math.ceil(channels.length / PAGE_SIZE));
    const pagedChannels = channels.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const rangeStart = channels.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(currentPage * PAGE_SIZE, channels.length);

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Channels</h1>
                <p className="mt-1 text-sm text-gray-500">Company wise configurable master values.</p>
            </div>

            <div className="flex flex-wrap gap-3">
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${activeTab === tab.key
                            ? "border-[#7f35b2] bg-white text-[#7f35b2]"
                            : "border-gray-200 bg-white text-[#7f35b2] hover:bg-[#c2a8f033]"
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab !== "channels" ? (
                <div className="rounded-2xl border border-gray-100 bg-white p-10 text-center text-sm text-gray-400 shadow-sm">
                    Coming soon - only the Channels tab is functional at this time.
                </div>
            ) : (
                <>
                    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <h2 className="text-base font-semibold text-gray-900">Channels</h2>
                                <p className="mt-1 text-sm text-gray-500">Manage the available channels used across the platform.</p>
                            </div>
                            <Link
                                to="/dashboard/MasterData/add-channel"
                                className="flex items-center justify-center gap-1.5 rounded-lg bg-[#7f35b2] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#3bb290]"
                            >
                                <Plus size={16} />
                                Add Channel
                            </Link>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                        <div className="px-4 pt-4">
                            <h2 className="text-base font-semibold text-gray-900">Channels List</h2>
                        </div>
                        <div className="mt-3 overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-400">
                                    <tr className="border-y border-gray-100">
                                        <th className="px-4 py-3 font-semibold">Name</th>
                                        <th className="px-4 py-3 font-semibold">Status</th>
                                        <th className="px-4 py-3 font-semibold text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={3} className="px-6 py-10 text-center text-gray-400">
                                                Loading channels...
                                            </td>
                                        </tr>
                                    ) : pagedChannels.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="px-6 py-10 text-center text-gray-400">
                                                No channels found.
                                            </td>
                                        </tr>
                                    ) : (
                                        pagedChannels.map((channel) => {
                                            // status: 1 = Active, 0 = Inactive. Defaults to Active if the API
                                            // doesn't send this field yet.
                                            const isActive = channel.isactive === true;

                                            return (
                                                <tr key={channel.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                                                    <td className="px-4 py-3">
                                                        <span className="font-semibold text-gray-900">{channel.value}</span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span
                                                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                                                                isActive
                                                                    ? "bg-[#c2a8f033] text-[#7f35b2]"
                                                                    : "bg-orange-50 text-orange-500"
                                                            }`}
                                                        >
                                                            {isActive ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                navigate(`/dashboard/MasterData/edit-channel/${channel.id}`, {
                                                                    state: {
                                                                        channelId: channel.id,
                                                                        channelName: channel.value,
                                                                        status: isActive ? 1 : 0,
                                                                    },
                                                                })
                                                            }
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-blue-600 hover:bg-gray-50"
                                                            title="Edit channel"
                                                        >
                                                            <Pencil size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex flex-col items-center gap-3 border-t border-gray-100 px-4 py-4 sm:flex-row sm:justify-between">
                            <span className="text-sm text-gray-500">
                                Showing {rangeStart} to {rangeEnd} of {channels.length} channels
                            </span>
                            <div className="flex items-center gap-1.5">
                                <button
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((page) => page - 1)}
                                    className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#7f35b2] text-sm font-medium text-white">
                                    {currentPage}
                                </span>
                                <button
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((page) => page + 1)}
                                    className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <Outlet context={{ refreshChannels: fetchChannels }} />
        </div>
    );
}

export default MasterData;
